/**
 * Seed Tag Modal Component
 *
 * Follows the same pattern as Technology and Asset sections for UI consistency.
 * Uses shared AddTag component for consistent UX across the platform.
 *
 * Key features:
 * - Supports bulk tag operations for multiple seeds
 * - Handles Asset, WebApplication, and ADObject seed types
 * - Fetches current seed data before update to prevent data loss
 * - Proper error handling with toast notifications
 */

import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { AddTag } from '@/sections/AddTag';
import { useMutation } from '@/utils/api';
import {
  tagAddedError,
  tagAddedSuccess,
  tagValidationError,
} from '@/utils/tagToastMessages';
import { validateTagInput } from '@/utils/tagValidation';

interface Props {
  open: boolean;
  onClose: () => void;
  seedKeys: string[];
  currentTags?: string[];
  onSuccess?: () => void;
}

/**
 * Modal component for adding tags to seeds
 *
 * Uses shared AddTag component for consistent UX across the platform.
 * Handles both single seed (from drawer) and bulk operations (from table).
 *
 * Note: Always fetches current seed data from backend to prevent data loss.
 * The currentTags prop is deprecated and no longer used.
 *
 * Seeds can be different types (Asset, WebApplication, ADObject), so we
 * need to determine the correct endpoint based on the seed's class field.
 *
 * Example usage:
 * ```tsx
 * <SeedTag
 *   open={modalState.tagModal}
 *   onClose={modalHandlers.closeTagModal}
 *   seedKeys={selectedKeys}
 *   onSuccess={clearSelection}
 * />
 * ```
 *
 * @param props - Component props
 * @returns Modal component
 */
export const SeedTag = ({
  open,
  onClose,
  seedKeys,
  currentTags: _currentTags = [], // Deprecated: No longer used, always fetches from backend
  onSuccess,
}: Props) => {
  const axios = useAxios();
  const { mutate: updateSeeds, status: updatingSeeds } = useMutation({
    defaultErrorMessage: 'Failed to add tag',
    mutationFn: async (newTag: string) => {
      const promises = seedKeys.map(async seedKey => {
        // ALWAYS fetch current seed to get tags AND status
        // Backend requires status to be included when updating tags
        const currentSeed = await axios.get(`/my`, {
          params: {
            label: 'seed',
            key: seedKey,
            exact: true,
          },
        });

        const seed = currentSeed.data.seeds?.[0];
        const existingTags = seed?.tags || [];
        const currentStatus = seed?.status || 'A';
        const seedClass = seed?.class || 'asset';

        // Determine endpoint based on seed class
        // Seeds can be: asset, webapplication, or adobject
        let endpoint = '/seed';
        if (seedClass === 'webapplication') {
          endpoint = '/webapplication';
        }

        const payload = {
          key: seedKey,
          status: currentStatus, // Include current status (required by backend)
          tags: [...existingTags, newTag],
        };

        return axios.put(endpoint, payload);
      });
      await Promise.all(promises);

      // Invalidate seed queries to refresh table
      // Uses predicate-based invalidation matching Technology pattern
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'seed';
        },
        refetchType: 'active', // Only refetch visible queries for better performance
      });
    },
  });

  const handleSubmit = async (tag: string) => {
    // Validate input
    const validation = validateTagInput(tag);
    if (!validation.isValid) {
      toast.error(tagValidationError(validation.error!));
      return;
    }

    try {
      // Use sanitized value
      await updateSeeds(validation.sanitized!);

      // Dynamic message based on count
      const count = seedKeys.length > 1 ? seedKeys.length : undefined;
      toast.success(tagAddedSuccess('seed', count));

      onSuccess?.();
    } catch (error: any) {
      console.error('[SeedTagModal] Tag operation failed:', error);
      toast.error(
        tagAddedError('seed', error.response?.data?.message || error.message)
      );
    }
  };

  return (
    <AddTag
      open={open}
      onClose={onClose}
      disabled={updatingSeeds === 'pending'}
      onSubmit={handleSubmit}
      description="Tags help organize and classify seeds in your attack surface."
    />
  );
};
